import type { JSX, ParentProps } from 'solid-js';

// Collapsible section using native <details>/<summary>. Semantic,
// accessible out of the box, keyboard-navigable, screen-reader friendly.
//
// Uncontrolled by default (uses defaultOpen). Pass both `open` and
// `onToggle` to make it controlled — required when the section lives
// inside a reactive list (e.g. <For each={groupedByCategory()}>) so the
// expanded state survives re-renders of the list. Without controlled
// mode, every filter change destroys and recreates the <details> node,
// losing its open attribute.

export default function CollapsibleSection(
  props: ParentProps<{
    title: string;
    subtitle?: string;
    defaultOpen?: boolean;
    count?: number;
    open?: boolean;
    onToggle?: (open: boolean) => void;
  }>,
): JSX.Element {
  const isOpen = () =>
    props.open !== undefined ? props.open : props.defaultOpen !== false;
  return (
    <details
      open={isOpen()}
      class="group"
      onToggle={(e) => {
        if (props.onToggle) props.onToggle(e.currentTarget.open);
      }}
    >
      <summary class="list-none cursor-pointer select-none flex items-baseline gap-3 py-2 border-b border-brand-hex/70">
        <svg
          class="w-3 h-3 shrink-0 transition-transform group-open:rotate-90 text-brand-green"
          viewBox="0 0 12 12"
          aria-hidden="true"
        >
          <path d="M4 2 L8 6 L4 10" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
        <h2 class="font-display text-xl font-semibold">{props.title}</h2>
        {props.count != null && (
          <span class="text-xs text-brand-green/60">
            {props.count} {props.count === 1 ? 'item' : 'items'}
          </span>
        )}
        {props.subtitle && (
          <span class="text-xs text-brand-green/60 ml-auto">{props.subtitle}</span>
        )}
      </summary>
      <div class="pt-4">{props.children}</div>
    </details>
  );
}
